import { useSearchCommon } from "../../../hooks/useSearchCommon";
import { generateSearchListingsQueryKey } from "../utils/generateSearchListingsQueryKey";
import { usePagedNsSearchListingsQuery } from "./usePagedNsSearchListingsQuery";
import { useInfiniteSearchListingsQuery } from "./useInfiniteSearchListingsQuery";
import { useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { leadsApi } from "../../../libs/landvoice-api";
import { getListingFromLead, convertPrivatelyOwnedToCompanyOwned, getYearBuiltMax } from "../../../utils/neighborhoodSearch";
import { PROPERTY_TYPE_OPTIONS, OWNER_OCCUPANCY_FILTER, OwnerOccupancyFilter } from "../constants";
import { 
  BEDS_MIN,
  BEDS_MAX, 
  BATHS_MIN,
  BATHS_MAX, 
  SQFT_MIN,
  SQFT_MAX, 
  ACREAGE_MIN,
  ACREAGE_MAX, 
  YEAR_BUILT_MIN
} from "../../../constants/neighborhoodSearch";
import { getMinMaxQueryParams } from "../utils/getMinMaxQueryParams";
import { isNullOrUndefined } from "src/utils/objects";

interface ActiveFilter {
  label: string;
  delete?: () => void;
}

interface UseNeighborhoodSearchParams {
  selectedIds?: string[];
  beds?: [number | null, number | null];
  baths?: [number | null, number | null];
  acreage?: [number | null, number | null];
  sqft?: [number | null, number | null];
  yearBuilt?: [number | null, number | null];
  selectedPropertyTypes?: string[];
  isShowOnlyPrivatelyOwned?: boolean | null | undefined;
  isFavorite?: boolean | null | undefined;
  isHideDNCPhoneNumbers?: boolean | null | undefined;
  ownerOccupied?: OwnerOccupancyFilter;
  searchByTags?: string[];
  searchByNotes?: string[];
  // Setter functions for updating local state
  setBeds?: (beds: [number | null, number | null]) => void;
  setBaths?: (baths: [number | null, number | null]) => void;
  setAcreage?: (acreage: [number | null, number | null]) => void;
  setSqft?: (sqft: [number | null, number | null]) => void;
  setYearBuilt?: (yearBuilt: [number | null, number | null]) => void;
  setSelectedPropertyTypes?: (types: string[]) => void;
  setIsFavorite?: (favorite: boolean | undefined | null) => void;
  setIsShowOnlyPrivatelyOwned?: (privatelyOwned: boolean | undefined | null) => void;
  setIsHideDNCPhoneNumbers?: (hideDNC: boolean | undefined | null) => void;
  setOwnerOccupied?: (ownerOccupied: OwnerOccupancyFilter) => void;
  setSearchByTags?: (tags: string) => void;
  setSearchByNotes?: (notes: string) => void;
}

// Helper function to prepare common query parameters
const prepareQueryParams = ({
  selectedIds,
  beds,
  baths,
  acreage,
  sqft,
  yearBuilt,
  selectedPropertyTypes,
  isShowOnlyPrivatelyOwned,
  isFavorite,
  isHideDNCPhoneNumbers,
  ownerOccupied,
  searchByTags,
  searchByNotes,
}: {
  selectedIds?: string[];
  beds?: [number | null, number | null];
  baths?: [number | null, number | null];
  acreage?: [number | null, number | null];
  sqft?: [number | null, number | null];
  yearBuilt?: [number | null, number | null];
  selectedPropertyTypes?: string[];
  isShowOnlyPrivatelyOwned?: boolean | null | undefined;
  isFavorite?: boolean | null | undefined;
  isHideDNCPhoneNumbers?: boolean | null | undefined;
  ownerOccupied?: OwnerOccupancyFilter;
  searchByTags?: string[];
  searchByNotes?: string[];
}) => ({
  listId: selectedIds,
  bedrooms: isNullOrUndefined(beds) ? undefined : [beds[0]?.toString() || "", beds[1]?.toString() || ""],
  bathrooms: isNullOrUndefined(baths) ? undefined : [baths[0]?.toString() || "", baths[1]?.toString() || ""],
  acreage: [acreage?.[0]?.toString() || "", acreage?.[1]?.toString() || ""],
  squareFootage: [sqft?.[0]?.toString() || "", sqft?.[1]?.toString() || ""],
  yearBuilt: [yearBuilt?.[0]?.toString() || "", yearBuilt?.[1]?.toString() || ""],
  propertyType: selectedPropertyTypes,
  companyOwned: convertPrivatelyOwnedToCompanyOwned(isShowOnlyPrivatelyOwned),
  favorite: isFavorite,
  tag: Array.isArray(searchByTags) ? searchByTags.join(",") : (searchByTags || ""),
  note: Array.isArray(searchByNotes) ? searchByNotes.join(",") : (searchByNotes || ""),
});

export const useSearchListingsNS = ({
  selectedIds = [],
  beds = undefined,
  baths = undefined,
  acreage = [null, null],
  sqft = [null, null],
  yearBuilt = [null, null],
  selectedPropertyTypes = [],
  isShowOnlyPrivatelyOwned = undefined,
  isFavorite = undefined,
  isHideDNCPhoneNumbers = undefined,
  ownerOccupied = OWNER_OCCUPANCY_FILTER.ALL,
  searchByTags = [],
  searchByNotes = [],
  setBeds,
  setBaths,
  setAcreage,
  setSqft,
  setYearBuilt,
  setSelectedPropertyTypes,
  setIsFavorite,
  setIsShowOnlyPrivatelyOwned,
  setIsHideDNCPhoneNumbers,
  setOwnerOccupied,
  setSearchByTags,
  setSearchByNotes,
}: UseNeighborhoodSearchParams = {}) => {

  // Define filter configurations for API mapping
  const filterConfigs = [
    {
      name: "currentPage",
      excludeFromSearchQuery: true,
    },
    {
      name: "pageSize",
      apiKeyName: "limit",
    },
    {
      name: "listId",
      apiKeyName: "list_id",
      prepareApiValue: (value: unknown) => Array.isArray(value) ? value.join(",") : value,
    },
    {
      name: "bedrooms",
      apiKeyName: "bedrooms",
      prepareApiValue: (value: unknown) => Array.isArray(value) ? value : [value],
    },
    {
      name: "bathrooms", 
      apiKeyName: "bathrooms",
      prepareApiValue: (value: unknown) => Array.isArray(value) ? value : [value],
    },
    {
      name: "acreage",
      apiKeyName: "acreage",
      prepareApiValue: (value: unknown) => Array.isArray(value) ? value : [value],
    },
    {
      name: "squareFootage",
      apiKeyName: "square_footage", 
      prepareApiValue: (value: unknown) => Array.isArray(value) ? value : [value],
    },
    {
      name: "yearBuilt",
      apiKeyName: "year_built",
      prepareApiValue: (value: unknown) => Array.isArray(value) ? value : [value],
    },
    {
      name: "propertyType",
      apiKeyName: "property_type",
      prepareApiValue: (value: unknown) => Array.isArray(value) ? value : [value],
    },
    {
      name: "companyOwned",
      apiKeyName: "company_owned",
    },
    {
      name: "favorite",
      apiKeyName: "favorite", 
    },
    {
      name: "showHidden",
      apiKeyName: "show_hidden",
    },
    {
      name: "onlyHidden", 
      apiKeyName: "only_hidden",
    },
    {
      name: "tag",
      apiKeyName: "tag",
      prepareApiValue: (value: unknown) => Array.isArray(value) ? value.join(",") : (value || ""),
    },
    {
      name: "note",
      apiKeyName: "note",
      prepareApiValue: (value: unknown) => Array.isArray(value) ? value.join(",") : (value || ""),
    },
  ];

  const {
    currentPage,
    pageSize,
    sortField,
    sortDir,
    customFilters,
    searchApiQueryParams,
    updateSearchParams,
    resetFilters,
    scrollToTop,
    searchParams
  } = useSearchCommon({
    defaultPageSize: 25,
    defaultSortField: "date",
    defaultSortDir: "desc",
    customFilters: {
      listId: selectedIds,
      bedrooms: isNullOrUndefined(beds) ? undefined : [beds[0]?.toString() || "", beds[1]?.toString() || ""],
      bathrooms: isNullOrUndefined(baths) ? undefined : [baths[0]?.toString() || "", baths[1]?.toString() || ""],
      acreage: [acreage[0]?.toString() || "", acreage[1]?.toString() || ""],
      squareFootage: [sqft[0]?.toString() || "", sqft[1]?.toString() || ""],
      yearBuilt: [yearBuilt[0]?.toString() || "", yearBuilt[1]?.toString() || ""],
      propertyType: selectedPropertyTypes,
      companyOwned: convertPrivatelyOwnedToCompanyOwned(isShowOnlyPrivatelyOwned),
      favorite: isFavorite,
      hideDNCPhoneNumbers: isHideDNCPhoneNumbers,
      ownerOccupied: ownerOccupied,
      tag: Array.isArray(searchByTags) ? searchByTags : [],
      note: Array.isArray(searchByNotes) ? searchByNotes : [],
    },
    filterConfigs,
  });

  // Prepare common query parameters
  const queryParams = prepareQueryParams({
    selectedIds,
    beds,
    baths,
    acreage,
    sqft,
    yearBuilt,
    selectedPropertyTypes,
    isShowOnlyPrivatelyOwned,
    isFavorite,
    isHideDNCPhoneNumbers,
    ownerOccupied,
    searchByTags,
    searchByNotes,
  });

  // Use the infinite query for sidebar listings
  const infiniteSearchListingsQuery = useInfiniteSearchListingsQuery(queryParams);

  // Use the regular query for main listings
  const allSearchListingsQuery = usePagedNsSearchListingsQuery({
    ...queryParams,
    currentPage: 1,
    pageSize: 10000,
  });

  // Use the regular query for main listings
  const pagedSearchListingsQuery = usePagedNsSearchListingsQuery({
    ...queryParams,
    currentPage,
    pageSize,
  });

  const createRangeFilterLabel = (
    fieldName: string,
    min: number | null,
    max: number | null,
    maxValue: number
  ): string => {
    if (min !== null && max !== null) {
      if (max === maxValue) {
        return `${fieldName}: ${min} or more`;
      } else {
        return `${fieldName}: ${min} - ${max}`;
      }
    } else if (min !== null) {
      return `${fieldName}: ${min} or more`;
    } else if (max !== null) {
      return `${fieldName}: up to ${max}`;
    }
    return '';
  };

  const activeFilters: ActiveFilter[] = useMemo(() => {
    const updatedActiveFilters: ActiveFilter[] = [];

    // Bedrooms filter
    if ((!isNullOrUndefined(beds) && beds?.length > 0)) {
      const label = createRangeFilterLabel('Bedrooms', beds[0], beds[1], BEDS_MAX);
      if (label) {
        updatedActiveFilters.push({
          label,
          delete: () => {
            updateSearchParams({ currentPage: 1, bedrooms: ['', ''] });
            setBeds?.([BEDS_MIN, BEDS_MAX]);
          },
        });
      }
    }

    // Bathrooms filter
    if ((!isNullOrUndefined(baths) && baths?.length > 0)) {
      const label = createRangeFilterLabel('Bathrooms', baths[0], baths[1], BATHS_MAX);
      if (label) {
        updatedActiveFilters.push({
          label,
          delete: () => {
            updateSearchParams({ currentPage: 1, bathrooms: ['', ''] });
            setBaths?.([BATHS_MIN, BATHS_MAX]);
          },
        });
      }
    }

    // Square footage filter
    if ((sqft[0] !== null && sqft[0] !== SQFT_MIN) || (sqft[1] !== null && sqft[1] !== SQFT_MAX)) {
      const label = createRangeFilterLabel('Square Footage', sqft[0], sqft[1], SQFT_MAX);
      if (label) {
        updatedActiveFilters.push({
          label,
          delete: () => {
            updateSearchParams({ currentPage: 1, squareFootage: ['', ''] });
            setSqft?.([SQFT_MIN, SQFT_MAX]);
          },
        });
      }
    }

    // Acreage filter
    if ((acreage[0] !== null && acreage[0] !== ACREAGE_MIN) || (acreage[1] !== null && acreage[1] !== ACREAGE_MAX)) {
      const label = createRangeFilterLabel('Acreage', acreage[0], acreage[1], ACREAGE_MAX);
      if (label) {
        updatedActiveFilters.push({
          label,
          delete: () => {
            updateSearchParams({ currentPage: 1, acreage: ['', ''] });
            setAcreage?.([ACREAGE_MIN, ACREAGE_MAX]);
          },
        });
      }
    }

    // Year built filter
    if ((yearBuilt[0] !== null && yearBuilt[0] !== YEAR_BUILT_MIN) || (yearBuilt[1] !== null && yearBuilt[1] !== getYearBuiltMax())) {
      const label = createRangeFilterLabel('Year Built', yearBuilt[0], yearBuilt[1], getYearBuiltMax());
      if (label) {
        updatedActiveFilters.push({
          label,
          delete: () => {
            updateSearchParams({ currentPage: 1, yearBuilt: ['', ''] });
            setYearBuilt?.([YEAR_BUILT_MIN, getYearBuiltMax()]);
          },
        });
      }
    }

    // Property types filter
    if (selectedPropertyTypes.length > 0) {
      const propertyTypeLabels = selectedPropertyTypes.map(typeValue => {
        const propertyType = PROPERTY_TYPE_OPTIONS.find(option => option.value === typeValue);
        return propertyType ? propertyType.label : typeValue;
      });
      updatedActiveFilters.push({
        label: `Property Types: ${propertyTypeLabels.join(', ')}`,
        delete: () => {
          updateSearchParams({ currentPage: 1, propertyType: [] });
          setSelectedPropertyTypes?.([]);
        },
      });
    }

    // Favorite filter
    if (isFavorite === true) {
      updatedActiveFilters.push({
        label: 'Favorites Only',
        delete: () => {
          updateSearchParams({ currentPage: 1, favorite: false });
          setIsFavorite?.(undefined);
        },
      });
    }

    // Company owned filter
    if (isShowOnlyPrivatelyOwned === true) {
      updatedActiveFilters.push({
        label: 'Privately Owned Only',
        delete: () => {
          updateSearchParams({ currentPage: 1, companyOwned: false });
          setIsShowOnlyPrivatelyOwned?.(undefined);
        },
      });
    }

    // Hide DNC Phone Numbers filter
    if (isHideDNCPhoneNumbers === true) {
      updatedActiveFilters.push({
        label: 'Hide DNC Phone Numbers',
        delete: () => {
          updateSearchParams({ currentPage: 1, hideDNCPhoneNumbers: false });
          setIsHideDNCPhoneNumbers?.(undefined);
        },
      });
    }

    // Owner occupancy filter
    if (ownerOccupied && ownerOccupied !== OWNER_OCCUPANCY_FILTER.ALL) {
      updatedActiveFilters.push({
        label: ownerOccupied === OWNER_OCCUPANCY_FILTER.OWNER ? "Owner Occupied Only" : "Non-Owner Occupied Only",
        delete: () => {
          updateSearchParams({ currentPage: 1, ownerOccupied: OWNER_OCCUPANCY_FILTER.ALL });
          setOwnerOccupied?.(OWNER_OCCUPANCY_FILTER.ALL);
        },
      });
    }

    // Tags filter
    if (searchByTags.length > 0) {
      updatedActiveFilters.push({
        label: `Tags: ${searchByTags.join(', ')}`,
        delete: () => {
          updateSearchParams({ currentPage: 1, tag: [] });
          setSearchByTags?.("");
        },
      });
    }

    // Notes filter
    if (searchByNotes.length > 0) {
      updatedActiveFilters.push({
        label: `Notes: ${searchByNotes.join(', ')}`,
        delete: () => {
          updateSearchParams({ currentPage: 1, note: [] });
          setSearchByNotes?.("");
        },
      });
    }

    return updatedActiveFilters;
  }, [
    beds,
    baths,
    sqft,
    acreage,
    yearBuilt,
    selectedPropertyTypes,
    isFavorite,
    isShowOnlyPrivatelyOwned,
    isHideDNCPhoneNumbers,
    ownerOccupied,
    searchByTags,
    searchByNotes,
    updateSearchParams,
    setBeds,
    setBaths,
    setSqft,
    setAcreage,
    setYearBuilt,
    setSelectedPropertyTypes,
    setIsFavorite,
    setIsShowOnlyPrivatelyOwned,
    setIsHideDNCPhoneNumbers,
    setOwnerOccupied,
    setSearchByTags,
    setSearchByNotes,
  ]);

  // Create the all listings query key with queryType: "all-listings"
  const allListingsQueryKey = generateSearchListingsQueryKey({
    ...queryParams,
    queryType: "all-listings",
  });

  // Query to fetch all listings for printing, export, send cards, etc.
  const allListingsQuery = useQuery({
    queryKey: allListingsQueryKey,
    enabled: false,
    staleTime: Infinity,
    queryFn: async () => {
      const { data } = await leadsApi.getLeadsWithCount(
        queryParams.bedrooms ? getMinMaxQueryParams(
          Number(queryParams.bedrooms[0]),
          Number(queryParams.bedrooms[1]),
          BEDS_MIN,
          BEDS_MAX
        ) : undefined,
        queryParams.bathrooms ? getMinMaxQueryParams(
          Number(queryParams.bathrooms[0]),
          Number(queryParams.bathrooms[1]),
          BATHS_MIN,
          BATHS_MAX
        ) : undefined,
        getMinMaxQueryParams(
          Number(queryParams.acreage[0]),
          Number(queryParams.acreage[1]),
          ACREAGE_MIN,
          ACREAGE_MAX
        ),
        getMinMaxQueryParams(
          Number(queryParams.squareFootage[0]),
          Number(queryParams.squareFootage[1]),
          SQFT_MIN,
          SQFT_MAX
        ),
        getMinMaxQueryParams(
          Number(queryParams.yearBuilt[0]),
          Number(queryParams.yearBuilt[1]),
          YEAR_BUILT_MIN,
          getYearBuiltMax()
        ),
        queryParams.propertyType,
        queryParams.favorite ?? undefined,
        queryParams.tag,
        queryParams.note,
        queryParams.companyOwned,
        undefined, // showHidden
        undefined, // onlyHidden
        queryParams.listId,
        0,
        10000
      );
      let processedListings = (data.leads || []).map((lead) => getListingFromLead(lead));

      if (isHideDNCPhoneNumbers) {
        // Filter out contacts with DNC flag set to true
        processedListings = processedListings.map((listing) => ({
          ...listing,
          contacts: listing.contacts?.filter((contact) => !contact.dnc) || []
        }));
      }

      // Apply owner occupancy filter
      if (ownerOccupied && ownerOccupied !== OWNER_OCCUPANCY_FILTER.ALL) {
        processedListings = processedListings.filter((listing) => {
          if (ownerOccupied === OWNER_OCCUPANCY_FILTER.OWNER) {
            return listing.ownerOccupied === true;
          } else if (ownerOccupied === OWNER_OCCUPANCY_FILTER.NON_OWNER) {
            return listing.ownerOccupied === false;
          }
          return true;
        });
      }

      return {
        listings: processedListings,
        total_records: ((ownerOccupied && ownerOccupied !== OWNER_OCCUPANCY_FILTER.ALL) || isHideDNCPhoneNumbers)
          ? processedListings.length 
          : data.total_count,
      };
    },
  });

  const SEND_CARDS_CHUNK_SIZE = 200;

  const fetchLeadsChunk = useCallback(async (offset: number, limit: number) => {
    const { data } = await leadsApi.getLeadsWithCount(
      queryParams.bedrooms ? getMinMaxQueryParams(
        Number(queryParams.bedrooms[0]),
        Number(queryParams.bedrooms[1]),
        BEDS_MIN,
        BEDS_MAX
      ) : undefined,
      queryParams.bathrooms ? getMinMaxQueryParams(
        Number(queryParams.bathrooms[0]),
        Number(queryParams.bathrooms[1]),
        BATHS_MIN,
        BATHS_MAX
      ) : undefined,
      getMinMaxQueryParams(
        Number(queryParams.acreage[0]),
        Number(queryParams.acreage[1]),
        ACREAGE_MIN,
        ACREAGE_MAX
      ),
      getMinMaxQueryParams(
        Number(queryParams.squareFootage[0]),
        Number(queryParams.squareFootage[1]),
        SQFT_MIN,
        SQFT_MAX
      ),
      getMinMaxQueryParams(
        Number(queryParams.yearBuilt[0]),
        Number(queryParams.yearBuilt[1]),
        YEAR_BUILT_MIN,
        getYearBuiltMax()
      ),
      queryParams.propertyType,
      queryParams.favorite ?? undefined,
      queryParams.tag,
      queryParams.note,
      queryParams.companyOwned,
      undefined, // showHidden
      undefined, // onlyHidden
      queryParams.listId,
      offset,
      limit
    );
    let processedListings = (data.leads || []).map((lead) => getListingFromLead(lead));

    if (isHideDNCPhoneNumbers) {
      processedListings = processedListings.map((listing) => ({
        ...listing,
        contacts: listing.contacts?.filter((contact) => !contact.dnc) || []
      }));
    }

    if (ownerOccupied && ownerOccupied !== OWNER_OCCUPANCY_FILTER.ALL) {
      processedListings = processedListings.filter((listing) => {
        if (ownerOccupied === OWNER_OCCUPANCY_FILTER.OWNER) return listing.ownerOccupied === true;
        if (ownerOccupied === OWNER_OCCUPANCY_FILTER.NON_OWNER) return listing.ownerOccupied === false;
        return true;
      });
    }

    return processedListings;
  }, [queryParams, isHideDNCPhoneNumbers, ownerOccupied]);

  const fetchListingsInChunks = useCallback(async (
    onProgress: (completed: number, total: number) => void,
    cancelled: { current: boolean },
    totalRecords: number,
  ) => {
    if (totalRecords <= 0) return [];

    const totalChunks = Math.ceil(totalRecords / SEND_CARDS_CHUNK_SIZE);
    let completedChunks = 0;

    const chunkPromises = Array.from({ length: totalChunks }, (_, i) => {
      const offset = i * SEND_CARDS_CHUNK_SIZE;
      const limit = Math.min(SEND_CARDS_CHUNK_SIZE, totalRecords - offset);

      return fetchLeadsChunk(offset, limit).then((listings) => {
        if (cancelled.current) return [];
        completedChunks++;
        onProgress(completedChunks, totalChunks);
        return listings;
      });
    });

    const results = await Promise.all(chunkPromises);
    if (cancelled.current) return [];
    return results.flat();
  }, [fetchLeadsChunk]);

  // Apply client-side filters to listings
  const applyClientSideFilters = (listings: any[]) => {
    let filteredListings = [...listings];
    
    // Apply DNC filter
    if (isHideDNCPhoneNumbers) {
      filteredListings = filteredListings.map((listing) => ({
        ...listing,
        contacts: listing.contacts?.filter((contact: any) => !contact.dnc) || []
      }));
    }
    
    // Apply owner occupancy filter
    if (ownerOccupied && ownerOccupied !== OWNER_OCCUPANCY_FILTER.ALL) {
      filteredListings = filteredListings.filter((listing) => {
        if (ownerOccupied === OWNER_OCCUPANCY_FILTER.OWNER) {
          return listing.ownerOccupied === true;
        } else if (ownerOccupied === OWNER_OCCUPANCY_FILTER.NON_OWNER) {
          return listing.ownerOccupied === false;
        }
        return true;
      });
    }
    
    return filteredListings;
  };
  
  // Apply filters to all listings data
  const filteredAllListings = applyClientSideFilters(allSearchListingsQuery.data?.listings || []);
  const filteredPagedListings = applyClientSideFilters(pagedSearchListingsQuery.data?.listings || []);
  const filteredInfiniteListings = applyClientSideFilters(
    infiniteSearchListingsQuery?.data?.pages.flatMap(page => page.listings || []) || []
  );
  
  // Check if client-side filtering is active
  const isClientSideFilteringActive = (ownerOccupied && ownerOccupied !== OWNER_OCCUPANCY_FILTER.ALL) || isHideDNCPhoneNumbers;

  // Generate a key that changes when search criteria change (excluding pagination)
  // This is used to reset the selected listing when performing a new search
  const searchCriteriaKey = useMemo(() => {
    return JSON.stringify({
      selectedIds,
      beds,
      baths,
      acreage,
      sqft,
      yearBuilt,
      selectedPropertyTypes,
      isShowOnlyPrivatelyOwned,
      isFavorite,
      isHideDNCPhoneNumbers,
      ownerOccupied,
      searchByTags,
      searchByNotes,
      sortField,
      sortDir,
    });
  }, [
    selectedIds,
    beds,
    baths,
    acreage,
    sqft,
    yearBuilt,
    selectedPropertyTypes,
    isShowOnlyPrivatelyOwned,
    isFavorite,
    isHideDNCPhoneNumbers,
    ownerOccupied,
    searchByTags,
    searchByNotes,
    sortField,
    sortDir,
  ]);

  return {
    // useSearchCommon values
    currentPage,
    pageSize,
    sortField,
    sortDir,
    customFilters,
    searchApiQueryParams,
    updateSearchParams,
    resetFilters,
    scrollToTop,
    searchParams,
    
    // Active filters
    activeFilters,
    
    // All search listings
    allSearchListings: filteredAllListings,
    allSearchListingsTotalRecords: isClientSideFilteringActive
      ? filteredAllListings.length 
      : allSearchListingsQuery.data?.total_records || 0,
    allSearchListingsIsLoading: allSearchListingsQuery.isLoading,
    
    // Infinite search listings
    infiniteSearchListings: filteredInfiniteListings,
    infiniteSearchListingsIsLoading: infiniteSearchListingsQuery.isLoading,
    infiniteSearchListingsFetchNextPage: infiniteSearchListingsQuery.fetchNextPage,
    infiniteSearchListingsHasNextPage: infiniteSearchListingsQuery.hasNextPage,
    
    // Paged search listings
    pagedSearchListings: filteredPagedListings,
    pagedSearchListingsTotalRecords: isClientSideFilteringActive
      ? filteredAllListings.length 
      : pagedSearchListingsQuery.data?.total_records || 0,
    pagedSearchListingsIsLoading: pagedSearchListingsQuery.isLoading,
    
    // Print query
    allListingsQuery,
    fetchListingsInChunks,

    // Key that changes when search criteria change (excluding pagination)
    searchCriteriaKey,
  };
};
